import React, { useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import Helmet from 'react-helmet'

// Components
import {
  useStore,
  updatePathname,
  updatePopState,
  hideMobileNav
} from '@Store'
import TransitionMask from '@components/PageTransitionMask'
import Gridlines from '@components/Gridlines'
import Header from '@components/Header'
import SmoothScroll from '@components/SmoothScroll'

// Fonts
import National2Woff2 from '../../../static/fonts/National_2/national-2-regular.woff2'
import National2ExtraboldWoff2 from '../../../static/fonts/National_2/national-2-extrabold.woff2'
import SignifierLightWoff2 from '../../../static/fonts/Signifier/signifier-light.woff2'
import BravebisonWoff2 from '../../../static/fonts/Bravebison/bravebison.woff2'

// Styles
import { GlobalStyles } from '@styles/GlobalStyles.style'
import { font } from '@styles/vars/font.style'
import Footer from '@components/Footer'
import { updateResized } from '@Store/'
import debounce from 'lodash/debounce'
import { isDesktop, isTablet } from 'react-device-detect'
import Display from '@components/_OurWorks/CaseStudies/Display'
import Display2 from '@components/_Insights/Insights/Display'

const loadScript = (src, options = {}) => {
  if (typeof document === 'undefined') return

  const existingScript = document.querySelector(`script[src="${src}"]`)
  if (existingScript) return

  const script = document.createElement('script')
  script.src = src
  script.async = options.async !== false
  script.defer = options.defer !== false

  if (options.id) {
    script.id = options.id
  }

  document.body.appendChild(script)
}

const initGTM = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js'
  })

  loadScript('https://www.googletagmanager.com/gtm.js?id=GTM-5SMNSHG', {
    id: 'gtm-script'
  })
}

const Layout = ({ children, location }) => {
  const [store, dispatch] = useStore()
  const { smoothScroll: smoothScrollIsActive, showDisplayControls } = store

  const $header = useRef()
  const $footer = useRef()

  const handleBrowserNavigationInteraction = useCallback(() => {
    updatePathname(
      dispatch,
      window.location.pathname.charAt(0) === '/'
        ? window.location.pathname.substring(1)
        : window.location.pathname
    )

    updatePopState(dispatch)
  }, [dispatch])

  const setViewportHeight = useCallback(() => {
    if (typeof window === 'undefined') return

    const vh = window.innerHeight * 0.01
    document.documentElement.style.setProperty('--vh', `${vh}px`)
  }, [])

  const setHeaderHeight = useCallback(() => {
    if (!$header.current) return

    document.documentElement.style.setProperty(
      '--headerHeight',
      `${$header.current.offsetHeight}px`
    )
  }, [])

  const setFooterHeight = useCallback(() => {
    if (!$footer.current) return

    document.documentElement.style.setProperty(
      '--footerHeight',
      `${$footer.current.offsetHeight}px`
    )
  }, [])

  const handleResize = useCallback(() => {
    setViewportHeight()
    setFooterHeight()
    setHeaderHeight()
  }, [setViewportHeight, setFooterHeight, setHeaderHeight])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const $content = document.querySelector('.tl-edges')

    handleResize()

    const resizeObserver = new ResizeObserver(
      debounce(() => {
        updateResized(dispatch)
      }, 50)
    )

    if ($content) {
      resizeObserver.observe($content)
    }

    window.addEventListener('resize', handleResize)
    window.addEventListener('popstate', handleBrowserNavigationInteraction)

    return () => {
      if ($content) {
        resizeObserver.unobserve($content)
      }

      resizeObserver.disconnect()

      window.removeEventListener('resize', handleResize)
      window.removeEventListener('popstate', handleBrowserNavigationInteraction)
    }
  }, [dispatch, handleResize, handleBrowserNavigationInteraction])

  useEffect(() => {
    hideMobileNav(dispatch)
  }, [dispatch, location])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const loadThirdPartyScripts = () => {
      initGTM()

      loadScript('https://js-eu1.hs-scripts.com/25832621.js', {
        id: 'hs-script-loader'
      })
    }

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(loadThirdPartyScripts, { timeout: 3000 })
    } else {
      window.setTimeout(loadThirdPartyScripts, 3000)
    }
  }, [])

  const mainHandle = e => {
    const event1 = new CustomEvent('mainClicked', {
      detail: { targets: e.target }
    })

    window.dispatchEvent(event1)
  }

  const isOurWorkPage =
    location.pathname === '/our-work' ||
    location.pathname === '/our-work/' ||
    location.pathname === 'our-work'

  const isInsightsPage =
    location.pathname === '/insights' ||
    location.pathname === '/insights/' ||
    location.pathname === 'insights'

  return (
    <>
      <GlobalStyles />

      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1"
        />

        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href={National2Woff2}
          crossOrigin="anonymous"
        />

        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href={National2ExtraboldWoff2}
          crossOrigin="anonymous"
        />

        <link
          rel="preload"
          as="font"
          type="font/woff2"
          href={SignifierLightWoff2}
          crossOrigin="anonymous"
        />

        <style>{`
          @font-face {
            font-family: '${font.sans.family}';
            src: url(${National2Woff2}) format('woff2');
            font-weight: ${font.sans.weight.regular};
            font-style: normal;
            font-display: swap;
          }

          @font-face {
            font-family: '${font.sans.family}';
            src: url(${National2ExtraboldWoff2}) format('woff2');
            font-weight: ${font.sans.weight.extrabold};
            font-style: normal;
            font-display: swap;
          }

          @font-face {
            font-family: '${font.serif.family}';
            src: url(${SignifierLightWoff2}) format('woff2');
            font-weight: ${font.serif.weight.light};
            font-style: normal;
            font-display: swap;
          }

          @font-face {
            font-family: '${font.bravebison.family}';
            src: url(${BravebisonWoff2}) format('woff2');
            font-weight: ${font.bravebison.weight.extrabold};
            font-style: normal;
            font-display: swap;
          }
        `}</style>
      </Helmet>

      <Gridlines show={false} />

      {(isDesktop || isTablet) && <SmoothScroll callbacks={location} />}

      <TransitionMask />

      <Header ref={$header} pathname={location.pathname} />

      <div
        id="smooth-wrapper"
        className={smoothScrollIsActive ? 'smooth-scroll-is-active' : ''}
      >
        <div id="smooth-content" onClick={mainHandle}>
          {children}
        </div>
      </div>

      {showDisplayControls && isOurWorkPage && <Display mobile={true} />}

      {showDisplayControls && isInsightsPage && <Display2 mobile={true} />}

      <Footer ref={$footer} smoothScrollIsActive={smoothScrollIsActive} />

      <noscript
        dangerouslySetInnerHTML={{
          __html: `<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-5SMNSHG" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
        }}
      />
    </>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired
}

export default Layout